import {
  Camera,
  ExternalLink,
  FileText,
  FolderOpen,
  Paperclip,
  UploadCloud,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { ZoomableImage } from "@/components/shared/zoomable-image";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FormLabel } from "@/components/ui/form";

import type { ExistingReceipt } from "./use-expense-form";

interface ExpenseAttachmentsProps {
  selectedFiles: File[];
  existingReceipts: ExistingReceipt[];
  isSaving: boolean;
  onAddFiles: (files: FileList | File[]) => void;
  onRemoveSelectedFile: (index: number) => void;
  onRemoveExistingReceipt: (key: string) => void;
}

export function ExpenseAttachments({
  selectedFiles,
  existingReceipts,
  isSaving,
  onAddFiles,
  onRemoveSelectedFile,
  onRemoveExistingReceipt,
}: ExpenseAttachmentsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewImage, setPreviewImage] = useState<{
    url: string;
    name: string;
  } | null>(null);

  // Thumbnail previews for newly-selected images, before they're uploaded.
  const previewUrls = useMemo(
    () =>
      selectedFiles.map((f) =>
        f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      ),
    [selectedFiles],
  );
  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => url && URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  return (
    <div className="flex flex-col gap-2.5 rounded-lg border bg-muted/15 p-3.5">
      <div>
        <FormLabel className="text-sm font-semibold flex items-center gap-1.5">
          <Paperclip className="h-4 w-4 text-primary" />
          Lampiran Berkas / Bukti
        </FormLabel>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
          Format didukung: Gambar (PNG, JPG, WEBP, HEIC), PDF, Excel, Word, CSV,
          TXT (Maks. 15MB/berkas).
        </p>
      </div>

      {/* Drag and Drop Zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            onAddFiles(e.dataTransfer.files);
          }
        }}
        className={`flex flex-col items-center justify-center gap-2.5 rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
          isDragging
            ? "border-primary bg-primary/10"
            : "border-muted-foreground/25"
        }`}
      >
        <div className="rounded-full bg-primary/10 p-2 text-primary">
          <UploadCloud className="h-5 w-5" />
        </div>
        <p className="text-[11px] text-muted-foreground">
          Tarik & letakkan berkas di sini, atau pilih di bawah
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => fileInputRef.current?.click()}
          >
            <FolderOpen className="h-3.5 w-3.5" />
            Pilih Berkas
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => cameraInputRef.current?.click()}
          >
            <Camera className="h-3.5 w-3.5" />
            Ambil Foto
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.heic,.heif"
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              onAddFiles(e.target.files);
              e.target.value = "";
            }
          }}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              onAddFiles(e.target.files);
              e.target.value = "";
            }
          }}
        />
      </div>

      {/* File List Container */}
      <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
        {/* Existing Receipts List */}
        {existingReceipts.length > 0 && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
              <span>Berkas Tersimpan ({existingReceipts.length}):</span>
            </div>
            <div className="space-y-1">
              {existingReceipts.map((receipt) => (
                <div
                  key={receipt.key}
                  className="flex items-center justify-between gap-2 rounded-md border bg-background px-2.5 py-1.5 text-xs shadow-2xs"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-3.5 w-3.5 shrink-0 text-primary" />
                    {receipt.url ? (
                      <a
                        href={receipt.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate text-primary hover:underline font-medium"
                        title={receipt.name}
                      >
                        {receipt.name}
                      </a>
                    ) : (
                      <span
                        className="truncate font-medium"
                        title={receipt.name}
                      >
                        {receipt.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {receipt.url && (
                      <a
                        href={receipt.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary p-0.5"
                        title="Lihat berkas"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveExistingReceipt(receipt.key);
                      }}
                      disabled={isSaving}
                      title="Hapus berkas"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Files List */}
        {selectedFiles.length > 0 && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              <span>Berkas Baru ({selectedFiles.length}):</span>
            </div>
            <div className="space-y-1">
              {selectedFiles.map((file, idx) => (
                <div
                  key={`${file.name}-${idx}`}
                  className="flex items-center justify-between gap-2 rounded-md border border-emerald-300/80 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 px-2.5 py-1.5 text-xs"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {previewUrls[idx] ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewImage({
                            url: previewUrls[idx] as string,
                            name: file.name,
                          });
                        }}
                        className="shrink-0"
                        title="Lihat preview"
                      >
                        <img
                          src={previewUrls[idx]}
                          alt={file.name}
                          className="h-8 w-8 rounded object-cover"
                        />
                      </button>
                    ) : (
                      <FileText className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    )}
                    <span
                      className="truncate font-medium text-foreground"
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      ({(file.size / 1024).toFixed(0)} KB)
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveSelectedFile(idx);
                    }}
                    disabled={isSaving}
                    title="Batal lampirkan"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {existingReceipts.length === 0 && selectedFiles.length === 0 && (
          <div className="py-2 text-center text-xs text-muted-foreground">
            Belum ada berkas yang dilampirkan.
          </div>
        )}
      </div>

      <Dialog
        open={previewImage !== null}
        onOpenChange={(open) => !open && setPreviewImage(null)}
      >
        <DialogContent className="p-2 sm:max-w-2xl" showCloseButton={false}>
          <DialogTitle className="sr-only">
            Preview {previewImage?.name}
          </DialogTitle>
          {previewImage && (
            <ZoomableImage
              key={previewImage.url}
              src={previewImage.url}
              alt={previewImage.name}
              className="max-h-[80vh] w-full rounded-md object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
